/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Actions for Labels Import
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.3.0
 */

import { createAction } from '../../modules/app/createAction';
import { TWizardStorageClearStepsAction } from '../../modules/wizard/wizardTypes';
import {
    LABELS_IMPORT_CLEAR_WIZARD_DATA,
    LABELS_IMPORT_HANDLE_ERROR,
    LABELS_IMPORT_HANDLE_NEXT,
    LABELS_IMPORT_INIT_VALIDATED_LIST,
    LABELS_IMPORT_LOAD_LABELS_VALIDATION_FAILURE,
    LABELS_IMPORT_LOAD_LABELS_VALIDATION_START,
    LABELS_IMPORT_LOAD_LABELS_VALIDATION_STOP,
    LABELS_IMPORT_SET_FILE_ERROR,
    LABELS_IMPORT_SET_FILE_META,
    LABELS_IMPORT_SET_FILE_OBJECTS,
    LABELS_IMPORT_SET_FILE_UPLOADED,
    LABELS_IMPORT_SET_IS_STEP_VALID,
    LABELS_IMPORT_SET_LABELS_VALIDATION_INFO,
    LABELS_IMPORT_SET_SAVED_PARAMS,
    LABELS_IMPORT_SET_TOTAL_ROWS_COUNT,
    LABELS_IMPORT_START_EXPORT_ERRORS,
} from './labelsImportConstants';
import {
    ILabelsImportLoadLabelsValidationFailurePayload,
    ILabelsImportLoadLabelsValidationStopPayload,
    ILabelsImportSetFileErrorPayload,
    ILabelsImportSetFileMetaPayload,
    ILabelsImportSetFileObjectsPayload,
    ILabelsImportSetFileUploadedPayload,
    ILabelsImportSetIsStepValidPayload,
    ILabelsImportSetLabelsValidationInfoPayload,
    ILabelsImportSetSavedParamsPayload,
    ILabelsImportSetTotalRowsCountPayload,
    TLabelsImportClearWizardDataAction,
    TLabelsImportHandleError,
    TLabelsImportHandleNextAction,
    TLabelsImportInitValidatedList,
    TLabelsImportLoadLabelsValidationFailure,
    TLabelsImportLoadLabelsValidationStart,
    TLabelsImportLoadLabelsValidationStop,
    TLabelsImportSetFileErrorAction,
    TLabelsImportSetFileListObjects,
    TLabelsImportSetFileMetaAction,
    TLabelsImportSetFileUploadedAction,
    TLabelsImportSetIsStepValidAction,
    TLabelsImportSetLabelsValidationInfoAction,
    TLabelsImportSetSavedParams,
    TLabelsImportSetTotalRowsCountAction,
    TLabelsImportStartExportErrorsAction,
} from './labelsImportTypes';

/*
* -------------------------------------------
*           HANDLE NEXT
* -------------------------------------------
*/

export const labelsImportHandleNext = (): TLabelsImportHandleNextAction => createAction(LABELS_IMPORT_HANDLE_NEXT);

/*
* -------------------------------------------
*           META
* -------------------------------------------
*/

export const labelsImportSetFileMeta = (payload: ILabelsImportSetFileMetaPayload): TLabelsImportSetFileMetaAction =>
    createAction(LABELS_IMPORT_SET_FILE_META, payload);

export const labelsImportSetValidationInfo = (payload: ILabelsImportSetLabelsValidationInfoPayload):
    TLabelsImportSetLabelsValidationInfoAction => createAction(LABELS_IMPORT_SET_LABELS_VALIDATION_INFO, payload);


/*
* -------------------------------------------
*           FILE
* -------------------------------------------
*/

export const labelsImportSetFileError = (payload: ILabelsImportSetFileErrorPayload): TLabelsImportSetFileErrorAction =>
    createAction(LABELS_IMPORT_SET_FILE_ERROR, payload);

export const labelsImportSetFileObjects = (payload: ILabelsImportSetFileObjectsPayload):
    TLabelsImportSetFileListObjects => createAction(LABELS_IMPORT_SET_FILE_OBJECTS, payload);

export const labelsImportSetFileUploaded = (payload: ILabelsImportSetFileUploadedPayload):
    TLabelsImportSetFileUploadedAction => createAction(LABELS_IMPORT_SET_FILE_UPLOADED, payload);

/*
* -------------------------------------------
*           STEP VALIDATION
* -------------------------------------------
*/

export const labelsImportSetIsStepValid = (payload: ILabelsImportSetIsStepValidPayload):
    TLabelsImportSetIsStepValidAction => createAction(LABELS_IMPORT_SET_IS_STEP_VALID, payload);

/*
* -------------------------------------------
*           WIZARD CLEAR
* -------------------------------------------
*/
export const labelsImportClearWizardData = (): TLabelsImportClearWizardDataAction =>
    createAction(LABELS_IMPORT_CLEAR_WIZARD_DATA);

/*
* -------------------------------------------
*           EXPORT ERRORS
* -------------------------------------------
*/

export const labelsImportStartExportErrors = (): TLabelsImportStartExportErrorsAction =>
    createAction(LABELS_IMPORT_START_EXPORT_ERRORS);

/*
* -------------------------------------------
*           LABELS VALIDATION
* -------------------------------------------
*/

export const labelsImportLoadLabelsValidationFailure = (payload: ILabelsImportLoadLabelsValidationFailurePayload):
    TLabelsImportLoadLabelsValidationFailure => createAction(LABELS_IMPORT_LOAD_LABELS_VALIDATION_FAILURE, payload);

export const labelsImportLoadLabelsValidationStart = (): TLabelsImportLoadLabelsValidationStart =>
    createAction(LABELS_IMPORT_LOAD_LABELS_VALIDATION_START);

export const labelsImportLoadLabelsValidationStop = (payload: ILabelsImportLoadLabelsValidationStopPayload):
    TLabelsImportLoadLabelsValidationStop => createAction(LABELS_IMPORT_LOAD_LABELS_VALIDATION_STOP, payload);

/*
* -------------------------------------------
*           VALIDATED LIST
* -------------------------------------------
*/

export const labelsImportInitValidatedList = (): TLabelsImportInitValidatedList =>
    createAction(LABELS_IMPORT_INIT_VALIDATED_LIST);

/*
* -------------------------------------------
*           HANDLE ERROR
* -------------------------------------------
*/

export const labelsImportHandleError = (payload: any): TLabelsImportHandleError =>
    createAction(LABELS_IMPORT_HANDLE_ERROR, payload);

/*
* -------------------------------------------
*           STORAGE
* -------------------------------------------
*/

export const labelsImportSetSavedParams = (payload: ILabelsImportSetSavedParamsPayload): TLabelsImportSetSavedParams =>
    createAction(LABELS_IMPORT_SET_SAVED_PARAMS, payload);

/*
* -------------------------------------------
*           SET TOTAL ROWS COUNT
* -------------------------------------------
*/

export const labelsImportSetTotalRowsCount = (payload: ILabelsImportSetTotalRowsCountPayload):
    TLabelsImportSetTotalRowsCountAction => createAction(LABELS_IMPORT_SET_TOTAL_ROWS_COUNT, payload)


export type TLabelsImportActions = TLabelsImportClearWizardDataAction
    | TLabelsImportHandleNextAction
    | TLabelsImportLoadLabelsValidationFailure
    | TLabelsImportLoadLabelsValidationStop
    | TLabelsImportSetFileErrorAction
    | TLabelsImportSetFileMetaAction
    | TLabelsImportSetFileListObjects
    | TLabelsImportSetFileUploadedAction
    | TLabelsImportSetLabelsValidationInfoAction
    | TLabelsImportSetIsStepValidAction
    | TLabelsImportSetSavedParams
    | TLabelsImportSetTotalRowsCountAction
    | TLabelsImportStartExportErrorsAction
    | TWizardStorageClearStepsAction
    ;
